// The development page's "Moving in & out" tab (the demo's "10. DEVELOPMENT HUB" Moving In / Moving Out, "11. MOVE IN /
// MOVE OUT CHECKLISTS" and "41. BETWEEN TENANCIES"). One call, GET /properties/:id/moving, gives the stat row, a card per
// flat moving in, a card per flat moving out and the changeovers of this development. The checklists themselves are
// ticked on the tenancy page ("Open it"); "Write it" sends the welcome letter once a move-in is complete. On a
// changeover the only things kept here are the target date and the note (PATCH /changeovers/:unitId).
//
// ?sub=in | out | changeovers picks the sub-tab (the pills stay: they are navigation). Under them the list toolbar:
// find a flat or a name, export what the sub-tab shows, and the Filter card's "Show" — ?mf=open | done narrows the
// move-in / move-out cards to the outstanding or the finished ones, ?mf=vacating | progress the changeovers to one of
// the two lists; each choice carries its count.
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Check, DoorOpen, Mail, Megaphone, DoorClosed } from 'lucide-react';
import { useCan } from '../../hooks/usePermission';
import { StatTile } from '../charts/StatTile';
import { Tabs, Button, EmptyState, Input, Textarea, type MenuEntry } from '../ui';
import { ListToolbar } from '../ui/ListToolbar';
import { FilterMenu } from '../ui/FilterMenu';
import { FormError } from '../forms/common';
import { PhaseChip, ClosingStrip } from '../lettings/ChangeoversTab';
import { checklistsApi, PHASE_INFO, type MovingChangeover, type MovingInCard, type MovingOutCard } from '../../lib/checklists';
import { formatDate, formatMoneyExact } from '../../lib/viz';

type Sub = 'in' | 'out' | 'changeovers';
const VACATING = new Set(['NOTICE_GIVEN', 'ON_MARKET']);
const OUTSTANDING_SHOWN = 4;
type Mf = 'vacating' | 'progress' | 'open' | 'done';
/** Still to do on a move-in / move-out card (a finished move-in whose welcome letter is due counts as outstanding). */
const isActive = (r: MovingInCard | MovingOutCard) => r.status === 'OPEN' || ('welcomeDue' in r && !!r.welcomeDue);

export function MovingTab({ propertyId, params, setParam, onMenu }: {
  propertyId: string;
  params: URLSearchParams;
  /** Merge into the page's query string (?sub=, ?mf=). */
  setParam: (patch: Record<string, string | null>) => void;
  /** Hand the exports up to the page's ⋯ menu. */
  onMenu?: (m: MenuEntry[]) => void;
}) {
  const canCards = useCan('checklists.read');
  const canChangeovers = useCan('changeovers.read');
  const moving = useQuery({ queryKey: ['moving', propertyId], queryFn: () => checklistsApi.moving(propertyId) });
  const closing = useQuery({ queryKey: ['/checklists/closing-due', propertyId], queryFn: () => checklistsApi.closingDue(propertyId), enabled: canCards });
  const [q, setQ] = useState('');

  const SUBS = useMemo(() => ([['in', 'Moving in', canCards], ['out', 'Moving out', canCards], ['changeovers', 'Changeovers', canChangeovers]] as Array<[Sub, string, boolean]>).filter((x) => x[2]), [canCards, canChangeovers]);
  const sub = SUBS.find((x) => x[0] === params.get('sub'))?.[0] ?? SUBS[0]?.[0] ?? null;
  const mfRaw = params.get('mf');
  const mf = (['vacating', 'progress', 'open', 'done'] as const).find((x) => x === mfRaw) ?? null as Mf | null;
  const go = (k: Sub, extra: Record<string, string | null> = {}) => setParam({ sub: k === SUBS[0]?.[0] ? null : k, mf: null, ...extra });

  useEffect(() => {
    if (!onMenu) return;
    onMenu([
      canCards && { label: 'Export moving-in CSV', icon: 'Download', export: { path: '/checklists', page: 'checklists', query: { kind: 'MOVE_IN', propertyId }, filename: 'moving-in.csv' } },
      canCards && { label: 'Export moving-out CSV', icon: 'Download', export: { path: '/checklists', page: 'checklists', query: { kind: 'MOVE_OUT', propertyId }, filename: 'moving-out.csv' } },
      canChangeovers && { label: 'Export changeovers', icon: 'Download', export: { path: '/changeovers', page: 'changeovers', query: { propertyId }, filename: 'changeovers.csv' } },
    ]);
    return () => onMenu([]);
  }, [propertyId, onMenu, canCards, canChangeovers]);

  const m = moving.data;
  const t = m?.tiles;
  const term = q.trim().toLowerCase();
  const matches = (x: { unitReference: string; tenantName?: string; outgoing?: { tenants: string }; incoming?: { tenants: string } | null }) =>
    !term || [x.unitReference, x.tenantName, x.outgoing?.tenants, x.incoming?.tenants].some((v) => v?.toLowerCase().includes(term));

  // The Filter card's "Show" for the sub-tab open: its two lists, each with how many it holds now.
  const cards = sub === 'in' ? m?.movingIn : sub === 'out' ? m?.movingOut : null;
  const showOptions = sub === 'changeovers'
    ? [{ value: 'vacating', label: `Vacating and on the market (${(m?.changeovers ?? []).filter((c) => VACATING.has(c.phase) && matches(c)).length})` },
      { value: 'progress', label: `Changeovers in progress (${(m?.changeovers ?? []).filter((c) => !VACATING.has(c.phase) && matches(c)).length})` }]
    : [{ value: 'open', label: `Outstanding (${(cards ?? []).filter((r) => isActive(r) && matches(r)).length})` },
      { value: 'done', label: `Completed in the last 30 days (${(cards ?? []).filter((r) => !isActive(r) && matches(r)).length})` }];
  const shown = mf && showOptions.some((o) => o.value === mf) ? mf : null;

  if (!SUBS.length) return <div className="surface px-4 py-10 text-center text-[12.5px] text-ink-500">Your role does not include the move-in / move-out checklists or changeovers.</div>;
  return (
    <div className="space-y-2.5">
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 xl:grid-cols-6">
        {moving.isLoading && Array.from({ length: 6 }, (_, i) => <div key={i} className="surface h-[78px] animate-pulse" />)}
        {t && t.movingIn !== null && <StatTile compact label="Moving in" value={t.movingIn} hint="checklists open" onClick={() => go('in')} />}
        {t && t.movingOut !== null && <StatTile compact label="Moving out" value={t.movingOut} hint="checklists open" color={t.movingOut ? 'var(--color-violet-600)' : undefined} onClick={() => go('out')} />}
        {t && t.changeovers !== null && <StatTile compact label="Changeovers in progress" value={t.changeovers} hint="let agreed or keys back" active={sub === 'changeovers' && mf === 'progress'} onClick={() => go('changeovers', { mf: 'progress' })} />}
        {t && t.vacatingOnMarket !== null && <StatTile compact label="Vacating & on the market" value={t.vacatingOnMarket} hint="notice in, still occupied" status={t.vacatingOnMarket ? 'warning' : undefined} active={sub === 'changeovers' && mf === 'vacating'} onClick={() => go('changeovers', { mf: 'vacating' })} />}
        {t && t.daysEmptyTotal !== null && <StatTile compact label="Days empty" value={t.daysEmptyTotal} hint="across the empty flats" status={t.daysEmptyTotal > 14 ? 'warning' : undefined} onClick={() => go('changeovers')} />}
        {t && t.rentNotEarnedMinor !== null && <StatTile compact label="Rent not earned" value={formatMoneyExact(t.rentNotEarnedMinor, t.currency)} hint="at the asking rent" status={t.rentNotEarnedMinor ? 'critical' : undefined} onClick={() => go('changeovers')} />}
      </div>

      {SUBS.length > 1 && (
        <Tabs className="max-w-full" value={sub} onChange={(k) => go(k)} items={SUBS.map(([k, l]) => ({ key: k, label: l, count: k === 'in' ? t?.movingIn : k === 'out' ? t?.movingOut : t ? (t.changeovers ?? 0) + (t.vacatingOnMarket ?? 0) : null }))} />
      )}

      <ListToolbar standalone search={q} onSearch={setQ} searchPlaceholder="Flat or name…" fetching={moving.isFetching && !moving.isLoading}
        exportCsv={sub === 'changeovers' ? { path: '/changeovers', page: 'changeovers', query: { propertyId }, filename: 'changeovers.csv' }
          : { path: '/checklists', page: 'checklists', query: { kind: sub === 'in' ? 'MOVE_IN' : 'MOVE_OUT', propertyId }, filename: sub === 'in' ? 'moving-in.csv' : 'moving-out.csv' }}
        filters={<FilterMenu value={{ show: shown ? [shown] : [] }} onChange={(n) => setParam({ mf: n.show?.[0] ?? null })} groups={[{ key: 'show', label: 'Show', single: true, options: showOptions }]} />} />

      {moving.error && <FormError error={moving.error} />}
      {moving.isLoading && <div className="grid gap-3 md:grid-cols-2">{[0, 1, 2, 3].map((i) => <div key={i} className="surface h-40 animate-pulse" />)}</div>}
      {m && sub === 'in' && m.movingIn && <Cards kind="in" rows={m.movingIn.filter(matches)} filtered={!!term} only={shown === 'open' || shown === 'done' ? shown : null} />}
      {m && sub === 'out' && m.movingOut && <Cards kind="out" rows={m.movingOut.filter(matches)} filtered={!!term} only={shown === 'open' || shown === 'done' ? shown : null} />}
      {m && sub === 'changeovers' && m.changeovers && (
        <div className="space-y-2.5">
          {canCards && !!closing.data?.length && <ClosingStrip rows={closing.data} />}
          {(!shown || shown === 'vacating') && (
            <ChangeoverSection title="Vacating and on the market" note="Viewings can run while the outgoing tenant is still there, which is what keeps the void short."
              rows={m.changeovers.filter((c) => VACATING.has(c.phase) && matches(c))} empty="Nothing is on notice. Every let flat is staying put for now." propertyId={propertyId} />
          )}
          {(!shown || shown === 'progress') && (
            <ChangeoverSection title="Changeovers in progress" rows={m.changeovers.filter((c) => !VACATING.has(c.phase) && matches(c))} empty="Nothing between tenancies." propertyId={propertyId}
              lost={m.tiles.rentNotEarnedMinor ? formatMoneyExact(m.tiles.rentNotEarnedMinor, m.tiles.currency) : null} />
          )}
        </div>
      )}
    </div>
  );
}

function SectionHead({ title, n, extra }: { title: string; n?: number; extra?: string | null }) {
  return (
    <div className="mb-2 flex items-center gap-2">
      <h3 className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-ink-400">{title}</h3>
      <span className="h-px flex-1 bg-ink-100" />
      {extra && <span className="text-[11.5px] italic text-red-600">{extra}</span>}
      {n !== undefined && <span className="rounded-full bg-ink-100 px-2 py-0.5 text-[11px] font-medium text-ink-600">{n}</span>}
    </div>
  );
}

/** The Moving in / Moving out cards: what is still open first, then what finished in the last 30 days. */
function Cards({ kind, rows, filtered, only }: { kind: 'in' | 'out'; rows: Array<MovingInCard | MovingOutCard>; filtered: boolean; /** The Filter card's Show: only the outstanding or only the finished. */ only: 'open' | 'done' | null }) {
  const active = only === 'done' ? [] : rows.filter(isActive);
  const done = only === 'open' ? [] : rows.filter((r) => !isActive(r));
  if (!rows.length) {
    return <EmptyState icon={kind === 'in' ? 'DoorOpen' : 'DoorClosed'} title={filtered ? 'Nobody matches' : kind === 'in' ? 'Nobody moving in' : 'Nobody moving out'}
      body={filtered ? 'Try another flat or name.' : kind === 'in' ? 'A move-in checklist opens by itself when a tenancy is created.' : 'A move-out checklist opens by itself when notice is given or an end date is set.'} />;
  }
  return (
    <div className="space-y-2.5">
      {only !== 'done' && <section>
        <SectionHead title="Outstanding" n={active.length} />
        {active.length ? <div className="grid gap-3 md:grid-cols-2">{active.map((r) => <MoveCard key={r.checklistId} kind={kind} r={r} />)}</div>
          : <p className="surface px-4 py-6 text-center text-[12.5px] text-ink-500">Nothing outstanding — every {kind === 'in' ? 'move-in' : 'move-out'} here is finished.</p>}
      </section>}
      {only === 'done' && !done.length && <p className="surface px-4 py-6 text-center text-[12.5px] text-ink-500">Nothing finished in the last 30 days.</p>}
      {done.length > 0 && (
        <section>
          <SectionHead title="Completed in the last 30 days" n={done.length} />
          <div className="grid gap-3 md:grid-cols-2">{done.map((r) => <MoveCard key={r.checklistId} kind={kind} r={r} />)}</div>
        </section>
      )}
    </div>
  );
}

function MoveCard({ kind, r }: { kind: 'in' | 'out'; r: MovingInCard | MovingOutCard }) {
  const qc = useQueryClient();
  const canSend = useCan('checklists.complete');
  const [result, setResult] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: () => checklistsApi.sendWelcome(r.checklistId),
    onSuccess: (x) => {
      const bad = x.results.filter((y) => y.status !== 'SENT');
      setResult(`${x.sent} sent${bad.length ? ` · not sent: ${bad.map((b) => `${b.toEmail ?? 'no email'} — ${b.error}`).join('; ')}` : ''}`);
      void qc.invalidateQueries({ queryKey: ['moving'] }); void qc.invalidateQueries({ queryKey: ['/checklists/closing-due'] });
    },
    onError: (e: Error) => setResult(e.message),
  });
  const date = 'startDate' in r ? r.startDate : r.endDate;
  const welcomeDue = 'welcomeDue' in r && r.welcomeDue;
  const welcomeSent = 'welcomeSent' in r && r.welcomeSent;
  const finished = r.status === 'COMPLETE';
  const colour = finished ? '#2bb87c' : r.overdue ? '#dc2626' : kind === 'in' ? '#4f7fd6' : '#8b6fd6';
  const when = r.daysToDate === null ? null : finished ? null : r.daysToDate < 0 ? `${-r.daysToDate} day${r.daysToDate === -1 ? '' : 's'} ago` : r.daysToDate === 0 ? 'today' : `in ${r.daysToDate} day${r.daysToDate === 1 ? '' : 's'}`;
  const left = r.outstanding.length;
  return (
    <article className={`surface flex min-w-0 flex-col overflow-hidden ${finished && !welcomeDue ? 'opacity-80' : ''}`} style={{ boxShadow: `inset 3px 0 0 ${colour}` }}>
      <div className="flex items-start gap-3 px-4 pt-3.5">
        <div className="shrink-0">
          <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-ink-400">Flat</div>
          <div className="font-mono text-[14px] font-semibold text-ink-900">{r.unitReference}</div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13.5px] font-semibold text-ink-900">{r.tenantName || r.tenancyReference}</div>
          <div className="text-[12px] text-ink-500">
            {kind === 'in' ? 'Moves in' : 'Leaves'} {date ? <b className="font-medium text-ink-700">{formatDate(date)}</b> : <span className="text-ink-400">— no date yet</span>}
            {when && <span className={r.overdue ? 'font-medium text-red-600' : ''}> · {when}</span>}
          </div>
        </div>
        <span className="shrink-0 rounded-full px-2 py-[3px] text-[11px] font-medium" style={{ background: `${colour}1f`, color: colour }}>
          {finished ? (welcomeDue ? 'Welcome to send' : 'Completed') : r.overdue ? 'Date passed' : 'In progress'}
        </span>
      </div>

      <div className="px-4 pt-3">
        <div className="mb-1 flex items-baseline justify-between text-[11.5px]">
          <span className="font-medium text-ink-600">Lotus Living to finish</span>
          <span className="tabular-nums text-ink-500">{r.done} of {r.total} done</span>
        </div>
        <div className="h-1.5 overflow-hidden rounded-full bg-ink-100" role="progressbar" aria-valuemin={0} aria-valuemax={r.total} aria-valuenow={r.done} aria-label="Checklist progress">
          <span className="block h-full rounded-full" style={{ width: `${r.percent}%`, background: colour }} />
        </div>
      </div>

      {left > 0 && (
        <div className="px-4 pt-3">
          <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-ink-400">Outstanding · {left}</div>
          <ul className="space-y-0.5 text-[12px] text-ink-700">
            {r.outstanding.slice(0, OUTSTANDING_SHOWN).map((o) => <li key={o} className="flex items-start gap-1.5"><span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-ink-300" /><span className="min-w-0">{o}</span></li>)}
            {left > OUTSTANDING_SHOWN && <li className="pl-2.5 text-ink-400">…and {left - OUTSTANDING_SHOWN} more</li>}
          </ul>
        </div>
      )}

      {welcomeDue && (
        <div className="mx-4 mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-[12px] text-ink-700">
          <Mail className="h-3.5 w-3.5 shrink-0 text-emerald-700" />
          <span className="min-w-0 flex-1"><b className="font-semibold text-ink-900">Write to the tenant.</b> Everything is ticked — send the welcome email with their portal invite.</span>
          {canSend && <Button size="sm" disabled={send.isPending} onClick={() => send.mutate()}>Write it</Button>}
        </div>
      )}
      {welcomeSent && <div className="mx-4 mt-3 inline-flex items-center gap-1.5 text-[12px] font-medium text-emerald-700"><Check className="h-3.5 w-3.5" />Welcome email sent</div>}
      {result && <div className="mx-4 mt-2 text-[12px] font-medium text-emerald-800">{result}</div>}

      <div className="mt-auto flex items-center justify-between gap-2 px-4 pb-3 pt-3">
        <span className="truncate text-[11px] text-ink-400">{r.tenancyReference}</span>
        <Link to={`/app/tenancies/${r.tenancyId}?checklist=${kind}`} className="inline-flex shrink-0 items-center gap-1 rounded-xl border border-ink-200 px-3 py-1.5 text-[12px] font-medium text-ink-800 transition hover:bg-ink-50">
          Open it <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
    </article>
  );
}

function ChangeoverSection({ title, note, rows, empty, propertyId, lost }: { title: string; note?: string; rows: MovingChangeover[]; empty: string; propertyId: string; lost?: string | null }) {
  return (
    <section>
      <SectionHead title={title} n={rows.length} extra={lost ? `${lost} of rent lost to voids so far` : null} />
      {note && rows.length > 0 && <p className="mb-2 text-[12px] text-ink-500">{note}</p>}
      {rows.length ? <div className="space-y-3">{rows.map((c) => <ChangeoverCard key={`${c.unitId}:${c.targetBackOnMarket ?? ''}:${c.notes ?? ''}`} c={c} propertyId={propertyId} />)}</div>
        : <p className="surface px-4 py-6 text-center text-[12.5px] text-ink-500">{empty}</p>}
    </section>
  );
}

function Kpi({ k, v, s, warn }: { k: string; v: string; s: string; warn?: boolean }) {
  return (
    <div className="min-w-0 rounded-xl bg-ink-50 px-3 py-2">
      <div className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-ink-400">{k}</div>
      <div className={`truncate text-[15px] font-semibold tabular-nums ${warn ? 'text-red-600' : 'text-ink-900'}`}>{v}</div>
      <div className="truncate text-[11px] text-ink-500" title={s}>{s}</div>
    </div>
  );
}

/** One flat between tenancies (demo §41 coPanelHTML): the dates and the money, where it is up to, our part, the records. */
function ChangeoverCard({ c, propertyId }: { c: MovingChangeover; propertyId: string }) {
  const qc = useQueryClient();
  const canEdit = useCan('changeovers.update');
  const [target, setTarget] = useState(c.targetBackOnMarket ?? '');
  const [notes, setNotes] = useState(c.notes ?? '');
  const dirty = target !== (c.targetBackOnMarket ?? '') || notes !== (c.notes ?? '');
  const save = useMutation({
    mutationFn: () => checklistsApi.setTarget(c.unitId, { targetReletDate: target || null, note: notes || null }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['moving', propertyId] }); void qc.invalidateQueries({ queryKey: ['/changeovers'] }); },
  });
  const colour = PHASE_INFO[c.phase].colour;
  const leaving = c.outgoing.status === 'ENDED' || !!c.moveOut?.keysReturned;
  const who = c.incoming ? c.incoming.tenants || c.incoming.reference : c.offer?.applicant ?? c.application?.applicant ?? null;
  const daysSub = c.daysEmpty > 0 ? (c.nextTenancyStarts ? `until ${formatDate(c.nextTenancyStarts)}` : 'and counting') : c.projectedVoidDays !== null ? `${c.projectedVoidDays} planned` : leaving ? 'not empty yet' : 'still occupied';
  const nextStep = c.steps.find((s) => !s.done)?.key;
  return (
    <article className="surface overflow-hidden" style={{ borderLeft: `3px solid ${colour}` }}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 pt-3.5">
        <span className="font-mono text-[14px] font-semibold text-ink-900">{c.unitReference}</span>
        <span className="text-[12px] text-ink-500">{[c.unitName, c.bedrooms !== null ? `${c.bedrooms} bed` : null].filter(Boolean).join(' · ')}</span>
        <PhaseChip phase={c.phase} />
        <span className="ml-auto text-[11.5px] text-ink-500">{c.nDone} of {c.total} steps{c.next ? ` · next: ${c.next.toLowerCase()}` : ''}</span>
      </div>

      <div className="grid grid-cols-2 gap-2 px-4 pt-3 lg:grid-cols-4">
        <Kpi k="Last tenancy ends" v={c.lastTenancyEnds ? formatDate(c.lastTenancyEnds) : '—'} s={`${c.outgoing.tenants || c.outgoing.reference}${c.outgoing.status === 'ENDED' ? ' · left' : ''}`} />
        <Kpi k="Next tenancy starts" v={c.nextTenancyStarts ? formatDate(c.nextTenancyStarts) : '—'} s={who ?? (c.listing?.availableFrom ? `advertised as free from ${formatDate(c.listing.availableFrom)}` : 'nobody lined up yet')} />
        <Kpi k="Days empty" v={c.daysEmpty > 0 ? String(c.daysEmpty) : '—'} s={daysSub} warn={c.daysEmpty > 14} />
        <Kpi k="Rent not earned" v={formatMoneyExact(c.rentNotEarnedMinor, c.currency)} s={c.askMinor ? `at ${formatMoneyExact(c.askMinor, c.currency)} pcm` : 'no asking rent'} warn={c.rentNotEarnedMinor > 0} />
      </div>

      <div className="px-4 pt-4">
        <SectionHead title="Where it is up to" />
        <ol className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-4">
          {c.steps.map((s, i) => (
            <li key={s.key} className={`flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[12px] ${s.done ? 'border-transparent bg-ink-50 text-ink-700' : s.key === nextStep ? 'border-bronze-300 bg-bronze-50/60 text-ink-900' : 'border-ink-100 text-ink-400'}`}>
              <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full text-[10.5px] font-semibold" style={s.done ? { background: colour, color: 'var(--color-on-primary)' } : { background: 'var(--color-ink-100)', color: 'var(--color-ink-500)' }}>{s.done ? <Check className="h-3 w-3" /> : i + 1}</span>
              <span className="min-w-0 flex-1 truncate">{s.label}</span>
              {s.key === nextStep && <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-bronze-700">next</span>}
            </li>
          ))}
        </ol>
      </div>

      <div className="px-4 pt-4">
        <SectionHead title="Our part" />
        <div className="grid gap-2 sm:grid-cols-[minmax(0,220px)_minmax(0,1fr)]">
          <label className="block min-w-0">
            <span className="mb-1 block text-[11.5px] font-medium text-ink-600">Aiming to be back on the market by</span>
            <Input type="date" value={target} onChange={(e) => setTarget(e.target.value)} disabled={!canEdit} className={`w-full ${c.targetPassed ? '!border-red-300' : ''}`} />
            {c.targetPassed && <span className="mt-0.5 block text-[11px] font-medium text-red-600">The date has passed</span>}
          </label>
          <label className="block min-w-0">
            <span className="mb-1 block text-[11.5px] font-medium text-ink-600">Notes on the changeover</span>
            <Textarea className="w-full" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!canEdit} placeholder={canEdit ? 'Carpet in the second bedroom, decorator booked for the 12th…' : 'No notes'} />
          </label>
        </div>
        {canEdit && dirty && (
          <div className="mt-2 flex items-center gap-2">
            <Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>Save</Button>
            <Button size="sm" variant="ghost" onClick={() => { setTarget(c.targetBackOnMarket ?? ''); setNotes(c.notes ?? ''); }}>Undo</Button>
          </div>
        )}
        <FormError error={save.error} />
      </div>

      <div className="px-4 pb-4 pt-4">
        <SectionHead title="The records behind it" />
        <div className="grid gap-2 sm:grid-cols-3">
          <RecordLink icon={<DoorClosed className="h-4 w-4" />} title="Moving Out" to={`/app/tenancies/${c.outgoing.tenancyId}?checklist=out`}
            sub={c.moveOut ? (c.moveOut.outstanding ? `${c.moveOut.outstanding} of ${c.moveOut.total} items left` : 'All ticked') : 'no checklist for this flat yet'} />
          <RecordLink icon={<Megaphone className="h-4 w-4" />} title="Marketing" to={c.listing ? `/app/listings/${c.listing.id}` : null}
            sub={c.listing ? `${c.listing.status === 'PUBLISHED' ? 'Advertised' : c.listing.status.toLowerCase().replace(/^\w/, (x) => x.toUpperCase())}${c.listing.advertisedRentMinor ? ` at ${formatMoneyExact(c.listing.advertisedRentMinor, c.currency)}` : ''}` : 'no advert yet'} />
          <RecordLink icon={<DoorOpen className="h-4 w-4" />} title="Moving In" to={c.incoming ? `/app/tenancies/${c.incoming.tenancyId}?checklist=in` : null}
            sub={c.incoming ? (c.moveIn ? (c.moveIn.outstanding ? `${c.moveIn.outstanding} of ${c.moveIn.total} items left` : 'All ticked') : c.incoming.reference) : 'nobody lined up yet'} />
        </div>
      </div>
    </article>
  );
}

function RecordLink({ icon, title, sub, to }: { icon: ReactNode; title: string; sub: string; to: string | null }) {
  const body = (
    <>
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-ink-100 text-ink-600">{icon}</span>
      <span className="min-w-0 flex-1"><b className="block text-[12.5px] font-semibold text-ink-900">{title}</b><i className="block truncate text-[11.5px] not-italic text-ink-500">{sub}</i></span>
      {to && <ArrowRight className="h-3.5 w-3.5 shrink-0 text-ink-300" />}
    </>
  );
  return to
    ? <Link to={to} className="flex min-w-0 items-center gap-2.5 rounded-xl border border-ink-100 px-3 py-2 transition hover:border-ink-300 hover:bg-ink-50">{body}</Link>
    : <div className="flex min-w-0 items-center gap-2.5 rounded-xl border border-ink-200 px-3 py-2 opacity-70">{body}</div>;
}
